import { Box, Button, FormField, Input, TextContent } from '@amzn/awsui-components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChallengePropAction,
  CLUE_DESCRIPTION_MAX_LENGTH,
  CLUE_DESCRIPTION_MIN_LENGTH,
  CLUE_TITLE_MAX_LENGTH,
  CLUE_TITLE_MIN_LENGTH,
  ClueFields,
  useCreateChallenge,
} from '../../../../store/create-challenge.context';
import { Clue } from '../../../../types/Challenge';
import { cloneDeep } from 'lodash';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { HorizontalRule } from '../../../common/HorizontalRule';
import { KeyValue } from '../../../common/KeyValue';
import MarkDownPreviewEditor from '@/src/components/common/MarkdownPreviewEditor/MarkDownPreviewEditor';
import useInputBuffer from '@/src/hooks/useInputBuffer';

interface ChallengeClueDetailProps {
  clue: Clue;
  clueNumber: number;
  onRemove: (clueNumber: number) => void;
  taskNumber: number;
}

const ChallengeClueDetail: React.FC<ChallengeClueDetailProps> = ({ clue, clueNumber, onRemove, taskNumber }) => {
  const { t } = useTranslation();
  const { handleUpdateChallengeClue, challengeClueValidator } = useCreateChallenge();

  const fieldPrefix = i18nKeys.challenges.subSections.tasks.subSections.fields.clues;
  const [descriptionErrorText, setDescriptionErrorText] = useState('');

  // Validate a typed field value directly (not the clue prop, which lags a
  // render behind the commit). Returns the error message, or '' when valid.
  const validateField = (field: ClueFields, typedValue: Partial<Clue>): string => {
    let error = '';
    const clueToValidate = Object.assign(cloneDeep(clue), typedValue);
    challengeClueValidator(
      clueToValidate,
      new Map<ClueFields, (err: string) => void>([[field, (err: string) => (error = err)]])
    ).isValidField(field);
    return error;
  };

  const titleInput = useInputBuffer(
    clue.title || '',
    (value) => handleUpdateChallengeClue(ChallengePropAction.CLUE_TITLE, taskNumber, clueNumber, value),
    { validate: (title) => validateField(ClueFields.TITLE, { title }) }
  );

  const handleDescriptionChange = (text: string) => {
    handleUpdateChallengeClue(ChallengePropAction.CLUE_DESCRIPTION, taskNumber, clueNumber, text);
    setDescriptionErrorText(validateField(ClueFields.DESCRIPTION, { description: text }));
  };
  return (
    <>
      <TextContent>
        <p>{t(i18nKeys.challenges.subSections.tasks.subSections.fields.clues.title, { count: Number(clueNumber) })}</p>
      </TextContent>
      <Box padding={'l'}>
        <Box float={'right'}>
          {clueNumber !== 1 && <Button iconName="close" variant="icon" onClick={() => onRemove(clueNumber)} />}
        </Box>
        <KeyValue className="primary-text" label={t(i18nKeys.challenges.challengeDetails.titles.title)}>
          <FormField
            errorText={titleInput.error}
            constraintText={`${t(fieldPrefix.fields.titleConstraint, {
              CLUE_TITLE_MIN_LENGTH,
              CLUE_TITLE_MAX_LENGTH,
            })}. ${t(i18nKeys.general.avoidPIIWarning)}`}
          >
            <Input
              data-testid={`challenge-task-${taskNumber}-clue-${clueNumber}__title-input`}
              onBlur={titleInput.handleBlur}
              onChange={({ detail }) => titleInput.handleChange(detail.value)}
              value={titleInput.value}
            />
          </FormField>
        </KeyValue>
        <FormField
          label={t(i18nKeys.challenges.challengeDetails.titles.description)}
          stretch
          errorText={descriptionErrorText}
          constraintText={`${t(fieldPrefix.fields.descriptionConstraint, {
            CLUE_DESCRIPTION_MIN_LENGTH,
            CLUE_DESCRIPTION_MAX_LENGTH,
          })}. ${t(i18nKeys.general.avoidPIIWarning)}`}
        >
          <MarkDownPreviewEditor
            data-testid={`challenge-task-${taskNumber}-clue-${clueNumber}__description-markdown`}
            onTextChanged={(text) => handleDescriptionChange(text)}
            text={clue.description || ''}
          />
        </FormField>
      </Box>
      <HorizontalRule />
    </>
  );
};

export default ChallengeClueDetail;
